/**
 * Encounter session timing — read/display helpers (mirrors medplum-server lib).
 */
import type { Encounter } from '@medplum/fhirtypes';
import { ENCOUNTER_SESSION_TIMING_EXT } from '@unify/fhir-model/constants';

export type SessionTimingMismatchCode =
  | 'actual-session-vs-service-type'
  | 'actual-session-vs-appointment'
  | 'actual-appointment-vs-appointment';

export interface EncounterSessionTiming {
  serviceTypeExpectedMinutes?: number;
  appointmentExpectedMinutes?: number;
  actualSessionMinutes?: number;
  actualAppointmentMinutes?: number;
  capturedAt: string;
  mismatches: SessionTimingMismatchCode[];
}

const MISMATCH_LABELS: Record<SessionTimingMismatchCode, string> = {
  'actual-session-vs-service-type':
    'Documented session length differs from the service type default (≥5 min).',
  'actual-session-vs-appointment':
    'Documented session length differs from the scheduled appointment length (≥5 min).',
  'actual-appointment-vs-appointment':
    'Total meeting time differs from the scheduled appointment length (≥5 min).',
};

export function readEncounterSessionTiming(encounter: Encounter | null | undefined): EncounterSessionTiming | undefined {
  const ext = encounter?.extension?.find((e) => e.url === ENCOUNTER_SESSION_TIMING_EXT);
  if (!ext?.valueString) return undefined;
  try {
    const parsed = JSON.parse(ext.valueString) as EncounterSessionTiming;
    if (!parsed?.capturedAt) return undefined;
    return {
      ...parsed,
      mismatches: Array.isArray(parsed.mismatches) ? parsed.mismatches : [],
    };
  } catch {
    return undefined;
  }
}

export function formatSessionTimingMismatchLabels(timing: EncounterSessionTiming): string[] {
  return timing.mismatches.map((code) => MISMATCH_LABELS[code] ?? code);
}
